/**
 * Disparo de TARP a partir de uma leitura/gatilho (GUPM-174). TS puro.
 * Fino sobre `tarpForTrigger` (171): converte o nível de uma leitura no
 * código do TARP a abrir. NORMAL / sem nível não dispara.
 */
import type { Canal, InstrumentType, TarpLevel } from '../types';
import { isTarpCode, tarpForTrigger, type TarpCode } from './catalog';
import { foraDeServico } from '../instrumentos/fora-de-servico';
import { INSTRUMENT_TYPE_ORDER } from '../instrumentos/instrument-type';
import { leituraGovernante, semSinalVisivel } from '../instrumentos/nivel-exibido';

/** TARP para um instrumento num dado nível; null se NORMAL / sem vigência. */
export function tarpForReading(level: TarpLevel | null, instrumentType?: string): TarpCode | null {
  if (!level || level === 'NORMAL') return null;
  return tarpForTrigger({ kind: 'instrumento', level, instrumentType });
}

/**
 * Gatilho que produziu a pré-seleção — GUPM-303. Viaja junto do contexto porque a tela
 * de destino precisa DIZER ao RT de onde vieram barragem e instrumento, e os dois
 * caminhos têm origens incompatíveis: um tem uma medição fora de faixa, o outro tem
 * silêncio. A legenda do modo notificação afirmava "leitura anômala" para os dois.
 *
 * Campo obrigatório em `AutoOpenContext` de propósito: faz o `tsc` enumerar os pontos de
 * construção em vez de depender de alguém lembrar — mesma política do parâmetro `semSinal`
 * em `nivel-exibido.ts`, e pelo mesmo motivo (este app já esqueceu de ligar regra nova em
 * ponto de render mais de uma vez).
 */
export type AutoOpenOrigem = 'leitura' | 'perda-de-sinal';

const ORIGENS: ReadonlySet<string> = new Set<AutoOpenOrigem>(['leitura', 'perda-de-sinal']);

/** Tipos de instrumento conhecidos — narrowing do `instrumentType` vindo do router state. */
const TIPOS: ReadonlySet<string> = new Set<string>(INSTRUMENT_TYPE_ORDER);

/** Contexto de auto-abertura (GUPM-185): o que pré-preenche o formulário da TARP. */
export interface AutoOpenContext {
  damId: string;
  instrumentId: string;
  instrumentCode: string;
  tarpCode: TarpCode;
  origem: AutoOpenOrigem;
  /**
   * C-88 — tipo do instrumento, para o título da TARP nomear o tipo e o fluxo do
   * tiltímetro trocar a releitura pela próxima leitura publicada. Opcional: o state de uma
   * aba aberta antes do deploy não tem o campo e continua válido.
   */
  instrumentType?: InstrumentType;
}

/**
 * A partir de um instrumento anômalo, monta o contexto para abrir a TARP já
 * pré-preenchida — barragem + instrumento + protocolo derivado por `tarpForReading`.
 * `null` só quando NORMAL/sem nível. Puro: a UI (dashboard → M3) apenas transporta este
 * contexto. Reconcilia com GUPM-174 (modo manual não fixa instrumento; o modo notificação
 * chega com instrumento confirmado).
 *
 * C-88 — o filtro "só PZ/INA" do GUPM-185 saiu. Ele existia porque o default de
 * `tarpForTrigger` era o protocolo do piezômetro; com o default genérico (INST-AT/AL) e o
 * TILT-EMG não há protocolo errado a evitar, e o filtro fazia o clique num tiltímetro em
 * Emergência cair na lista de TARPs sem fluxo (14/10/2026).
 */
export function autoOpenContextFor(
  inst: { id: string; damId: string; code: string; type: InstrumentType },
  level: TarpLevel | null,
): AutoOpenContext | null {
  const tarpCode = tarpForReading(level, inst.type);
  if (!tarpCode) return null;
  return {
    damId: inst.damId, instrumentId: inst.id, instrumentCode: inst.code, tarpCode,
    origem: 'leitura', instrumentType: inst.type,
  };
}

/**
 * Contexto de auto-abertura por PERDA DE SINAL — GUPM-303, generalizado no C-88.
 *
 * O tiltímetro tem protocolo próprio (`TARP-PS-TILT`: sensor de ruptura, sirenes); os
 * demais tipos usam o de instrumento geotécnico (`TARP-PS-INST`), como no protótipo.
 *
 * Leva o `instrumentId` de propósito: as três `TARP-PS-TILT` de produção têm
 * `instrument_id = NULL`, e na de 24/08 o RT precisou escrever o código do instrumento no
 * campo de observação livre porque o app não tinha esse caminho.
 */
export function autoOpenContextForPerdaSinal(
  inst: {
    id: string; damId: string; code: string; type: InstrumentType;
    /** GUPM-307 — obrigatório: silêncio declarado não abre protocolo de perda de sinal. */
    foraDeServicoDesde: Date | null;
  },
): AutoOpenContext | null {
  // GUPM-307 — a declaração decide ANTES do tipo. O instrumento está mudo porque alguém o
  // retirou de campo e registrou isso; abrir a TARP de perda de sinal pediria plano de
  // ação para trabalho que já está em curso.
  if (foraDeServico(inst)) return null;
  return {
    damId: inst.damId,
    instrumentId: inst.id,
    instrumentCode: inst.code,
    tarpCode: tarpForTrigger({
      kind: 'perda-sinal',
      source: inst.type === 'TILT' ? 'tiltimetros' : 'instrumento',
    }),
    origem: 'perda-de-sinal',
    instrumentType: inst.type,
  };
}

/**
 * Contexto do atalho "→ Fluxo" do Resumo Técnico, a partir de UM instrumento — GUPM-303.
 *
 * Existe porque o atalho tem **dois** caminhos de auto-open e a tela tinha de escolher
 * entre eles — e escolhia errado. Achado no smoke test de 26/08/2026: o chip virou botão
 * e continuou ligado no caminho da LEITURA, então o tiltímetro mudo levava ao módulo TARP
 * e parava ali. No dashboard porque o instrumento era procurado numa lista que só tem
 * anomalia de leitura; no mapa porque `autoOpenContextFor` exclui `TILT` de propósito.
 * Dois motivos, o mesmo sintoma, duas implementações divergentes de `abrirFluxo`.
 *
 * A precedência é a do estado, não a do código: **instrumento mudo não tem leitura atual
 * a classificar**, então a perda de sinal decide antes. C-88: um tiltímetro que publica e
 * está fora de faixa passou a abrir pela leitura (TILT-EMG / INST-AL / INST-AT).
 *
 * Recebe o **instrumento**, nunca o código: `TIL-01` existe nas três barragens, e resolver
 * por código pode despachar equipe para a estrutura errada. Quem parte de um id usa
 * `resolverAtalhoDeFluxo`.
 */
export function contextoDoAtalhoDeFluxo(
  inst: {
    id: string; damId: string; code: string; type: InstrumentType;
    isOnline: boolean | null;
    /** GUPM-307 — obrigatório: instrumento declarado fora de serviço não auto-abre nada. */
    foraDeServicoDesde: Date | null;
  },
  level: TarpLevel | null,
): AutoOpenContext | null {
  // GUPM-307 — a declaração decide primeiro, e aqui é o lugar CERTO para ela estar.
  //
  // Antes deste guard o invariante "declarado nunca abre TARP" era verdadeiro por
  // composição acidental: o botão "→ Fluxo" não renderiza porque
  // `ehAnomaliaAcionavel(FORA_DE_SERVICO)` é `false`. Mas a função que DECIDE o disparo
  // olhava `isOnline === false` cru, então qualquer chamador novo — ou uma divergência de
  // nível como a que já apareceu no `agruparInstrumentosPorBarragem` — reabria o alarme
  // falso, agora como abertura de TARP e não como cor errada. Apontado pela revisão de
  // contexto limpo do PR #188.
  if (foraDeServico(inst)) return null;
  // C-88 (revisão) — o caminho de perda de sinal segue `semSinalVisivel`, a MESMA regra que
  // decide se a tela mostra "sem sinal" (o portão do GUPM-303 só mostra para tiltímetro).
  // Com `isOnline === false` cru, um PZ/INA mudo — exibido com o nível da última leitura —
  // abriria a PS-INST (Atenção) para um instrumento exibido em Emergência. INA/PZ ficam
  // `is_online=false` em todo atraso de publicação da GPX (468 episódios de perda de sinal em
  // 7 dias, audit_log de produção, 14/10/2026). `isOnline === null` (nunca derivado) fica fora.
  if (semSinalVisivel(inst)) return autoOpenContextForPerdaSinal(inst);
  return autoOpenContextFor(inst, level);
}

/**
 * Por que o atalho não montou fluxo — C-88. Viaja até a tela de TARP para ela DIZER o
 * motivo: antes, a navegação ia sem state e o usuário caía na lista em silêncio (em
 * 14/10/2026 o operador entrou 6 vezes na tela em 13 minutos e nenhuma TARP foi gravada).
 */
export type MotivoSemFluxo = 'instrumento-nao-encontrado' | 'sem-nivel' | 'fora-de-servico';

const MOTIVOS_SEM_FLUXO: ReadonlySet<string> = new Set<MotivoSemFluxo>([
  'instrumento-nao-encontrado', 'sem-nivel', 'fora-de-servico',
]);

export type ResultadoAtalho =
  | { ok: true; ctx: AutoOpenContext }
  | { ok: false; motivo: MotivoSemFluxo; instrumentCode: string | null };

/**
 * Resolve o atalho "Abrir fluxo TARP" a partir do **id** do instrumento — C-88.
 *
 * Os quatro pontos de entrada (card de atenção, chip da tabela técnica, chip da tabela de
 * tiltímetros, mapa) resolviam por código, e 18 códigos se repetem entre as barragens em
 * produção (INA-01…INA-11A, PZ-01…03, TIL-01/02, MS01…04, RL-01): o atalho podia
 * pré-selecionar o instrumento da barragem errada. `nivelDe` vem de quem chama porque cada
 * tela tem a sua fonte do nível (a lista de anomalias no M1, as leituras no mapa).
 */
export function resolverAtalhoDeFluxo<
  I extends {
    id: string; damId: string; code: string; type: InstrumentType;
    isOnline: boolean | null; foraDeServicoDesde: Date | null;
  },
>(
  instrumentId: string,
  instruments: readonly I[],
  nivelDe: (inst: I) => TarpLevel | null,
): ResultadoAtalho {
  const inst = instruments.find((i) => i.id === instrumentId);
  if (!inst) return { ok: false, motivo: 'instrumento-nao-encontrado', instrumentCode: null };
  if (foraDeServico(inst)) return { ok: false, motivo: 'fora-de-servico', instrumentCode: inst.code };
  const ctx = contextoDoAtalhoDeFluxo(inst, nivelDe(inst));
  return ctx ? { ok: true, ctx } : { ok: false, motivo: 'sem-nivel', instrumentCode: inst.code };
}

/**
 * Resolve o atalho do card de SEM SINAL a partir do id — C-88. Par de
 * `resolverAtalhoDeFluxo` para o outro tipo de card: o motivo de não haver fluxo é decidido
 * aqui, e não deduzido na tela.
 */
export function resolverAtalhoDeSemSinal<
  I extends { id: string; damId: string; code: string; type: InstrumentType; foraDeServicoDesde: Date | null },
>(instrumentId: string, instruments: readonly I[]): ResultadoAtalho {
  const inst = instruments.find((i) => i.id === instrumentId);
  if (!inst) return { ok: false, motivo: 'instrumento-nao-encontrado', instrumentCode: null };
  if (foraDeServico(inst)) return { ok: false, motivo: 'fora-de-servico', instrumentCode: inst.code };
  // `autoOpenContextForPerdaSinal` só devolve `null` para fora de serviço, já tratado acima.
  const ctx = autoOpenContextForPerdaSinal(inst);
  return ctx ? { ok: true, ctx } : { ok: false, motivo: 'fora-de-servico', instrumentCode: inst.code };
}

const NIVEIS: ReadonlySet<string> = new Set<TarpLevel>(['NORMAL', 'ATENCAO', 'ALERTA', 'EMERGENCIA']);

/**
 * Nível que o atalho usa para um instrumento: o da leitura do PIOR canal com faixa — C-88.
 * O mapa pegava a primeira leitura do instrumento no array, que no tiltímetro pode ser a
 * temperatura (sempre NORMAL) ou o eixo calmo, e perdia o nível governante (GUPM-270).
 */
export function nivelParaAtalho(
  inst: { id: string; canaisComFaixa?: readonly Canal[] },
  readings: readonly { instrumentId: string; canal: Canal | null; level: string | null; ts: Date }[],
): TarpLevel | null {
  const governante = leituraGovernante(
    readings.filter((r) => r.instrumentId === inst.id),
    inst.canaisComFaixa ?? [],
  );
  const level = governante?.level;
  return level && NIVEIS.has(level) ? (level as TarpLevel) : null;
}

/** Nav-state do atalho que não montou fluxo — C-88. `readAtalhoSemFluxo` é o par de leitura. */
export function atalhoSemFluxoNavState(
  motivo: MotivoSemFluxo,
  instrumentCode: string | null,
): { tarpAtalhoSemFluxo: { motivo: MotivoSemFluxo; instrumentCode: string | null } } {
  return { tarpAtalhoSemFluxo: { motivo, instrumentCode } };
}

/** Lê o motivo do nav-state com narrowing (frontend.md #1); motivo desconhecido → `null`. */
export function readAtalhoSemFluxo(
  state: unknown,
): { motivo: MotivoSemFluxo; instrumentCode: string | null } | null {
  if (!state || typeof state !== 'object') return null;
  const a = (state as { tarpAtalhoSemFluxo?: unknown }).tarpAtalhoSemFluxo;
  if (!a || typeof a !== 'object') return null;
  const { motivo, instrumentCode } = a as Record<string, unknown>;
  if (typeof motivo !== 'string' || !MOTIVOS_SEM_FLUXO.has(motivo)) return null;
  return {
    motivo: motivo as MotivoSemFluxo,
    instrumentCode: typeof instrumentCode === 'string' ? instrumentCode : null,
  };
}

/** Frase do aviso na tela de TARP — diz o que aconteceu e aponta o caminho manual. */
export function mensagemSemFluxo(a: { motivo: MotivoSemFluxo; instrumentCode: string | null }): string {
  const quem = a.instrumentCode ?? 'do instrumento';
  const saida = 'Se houver anomalia, use os acionamentos manuais abaixo.';
  switch (a.motivo) {
    case 'instrumento-nao-encontrado':
      return `Não foi possível abrir o fluxo automaticamente: o instrumento não está na lista atual. ${saida}`;
    case 'fora-de-servico':
      return `${quem} está declarado fora de serviço pelo RT, e o atalho não abre TARP para ele. ${saida}`;
    case 'sem-nivel':
      return `Não foi possível abrir o fluxo de ${quem}: a leitura atual está normal ou sem nível. ${saida}`;
  }
}

/**
 * Lê o contexto de auto-open (GUPM-185) de um `state` desconhecido — o nav-state do
 * router com que o dashboard navega pro M3 — com narrowing, não cast cego (frontend.md
 * #1). Só devolve o contexto se todos os campos forem strings E `tarpCode` for um código
 * de TARP conhecido (`isTarpCode`); senão `null` (defesa contra state malformado/stale).
 * Puro: a UI (dashboard → M3) apenas transporta este contexto. (GUPM-185, review #101:
 * extraído do componente M3Tarp p/ o domínio + testado.)
 */
export function readAutoOpen(state: unknown): AutoOpenContext | null {
  if (!state || typeof state !== 'object') return null;
  const a = (state as { tarpAutoOpen?: unknown }).tarpAutoOpen;
  if (!a || typeof a !== 'object') return null;
  const { damId, instrumentId, instrumentCode, tarpCode, origem, instrumentType } =
    a as Record<string, unknown>;
  if (
    typeof damId === 'string' &&
    typeof instrumentId === 'string' &&
    typeof instrumentCode === 'string' &&
    typeof tarpCode === 'string' &&
    isTarpCode(tarpCode) &&
    // GUPM-303 — gatilho ausente/desconhecido rejeita o state INTEIRO, em vez de assumir
    // 'leitura' por omissão: assumir é como a legenda passou a mentir na TARP de perda de
    // sinal. State stale (aba aberta antes do deploy) volta ao fluxo manual, que pergunta.
    typeof origem === 'string' &&
    ORIGENS.has(origem)
  ) {
    const ctx: AutoOpenContext = {
      damId, instrumentId, instrumentCode, tarpCode, origem: origem as AutoOpenOrigem,
    };
    // C-88 — o tipo é opcional: ausente (aba antiga) ou desconhecido não rejeita o state,
    // só deixa a TARP com o título e o fluxo do catálogo.
    if (typeof instrumentType === 'string' && TIPOS.has(instrumentType)) {
      ctx.instrumentType = instrumentType as InstrumentType;
    }
    return ctx;
  }
  return null;
}

/**
 * Nav-state que o botão de emergência (header/dashboard) envia ao M3 para abrir a
 * TARP de Emergência (GUPM-190 T5). Mesmo padrão efêmero do auto-open, mas sem
 * instrumento (emergência é da estrutura). `readEmergencyIntent` é o par de leitura.
 */
export function emergencyNavState(): { tarpEmergency: true } {
  return { tarpEmergency: true };
}

/**
 * Lê a intenção de emergência do nav-state do router com narrowing (não cast cego,
 * frontend.md #1). `true` só quando `state.tarpEmergency === true`; senão `false`
 * (defesa contra state malformado/stale). Puro: a UI só transporta a intenção.
 */
export function readEmergencyIntent(state: unknown): boolean {
  if (!state || typeof state !== 'object') return false;
  return (state as { tarpEmergency?: unknown }).tarpEmergency === true;
}

/**
 * Nav-state que o painel "Acionamentos manuais" (dashboard) envia ao M3 para abrir
 * o fluxo de preenchimento do acionamento escolhido — o mesmo que clicar no card
 * dentro do M3 (GUPM-191 D2). Sem instrumento: acionamento manual não fixa
 * instrumento (o modo notificação/auto-open é que traz instrumento). `readManualAction`
 * é o par de leitura. Emergência tem contrato próprio (`emergencyNavState`).
 */
export function manualActionNavState(code: TarpCode): { tarpAction: TarpCode } {
  return { tarpAction: code };
}

/**
 * Lê o código de ação manual do nav-state com narrowing (não cast cego, frontend.md
 * #1). Devolve o `TarpCode` só quando `state.tarpAction` é um código conhecido
 * (`isTarpCode`); senão `null` (defesa contra state malformado/stale). Puro: a UI
 * (dashboard → M3) apenas transporta o código.
 */
export function readManualAction(state: unknown): TarpCode | null {
  if (!state || typeof state !== 'object') return null;
  const code = (state as { tarpAction?: unknown }).tarpAction;
  return typeof code === 'string' && isTarpCode(code) ? code : null;
}

/**
 * Nav-state que o Resumo de TARPs (dashboard) envia ao M3 para abrir uma TARP
 * específica JÁ ABERTA (GUPM-199): canal efêmero via router state (não query
 * string), mesmo padrão do auto-open/emergência. `readOpenTarpId` é o par de
 * leitura. Diferente do auto-open (que pré-preenche um formulário novo), aqui o
 * id aponta uma TARP existente — o M3 acha na lista e reusa o clique da lista
 * (read-only por padrão; baixa segue RT-only via RLS + gating do modal).
 */
export function openTarpNavState(id: string): { tarpOpenId: string } {
  return { tarpOpenId: id };
}

/**
 * Lê o id da TARP a abrir (deep-link) do nav-state, com narrowing (não cast cego,
 * frontend.md #1). Devolve o id só quando é string não-vazia; senão `null` (defesa
 * contra state stale/malformado). Puro: a UI (dashboard → M3) só transporta o id.
 */
export function readOpenTarpId(state: unknown): string | null {
  if (!state || typeof state !== 'object') return null;
  const id = (state as { tarpOpenId?: unknown }).tarpOpenId;
  return typeof id === 'string' && id.length > 0 ? id : null;
}
